import React from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { ThemeOptions } from '@mui/material';
import { makeStyles } from '@mui/styles';
import AccordionPanel from '@/components/UI/AccordionPanel/AccordionPanel';
import { ContainerGrid, ContainerGridItem } from '@/components/UI/ContainerGrid/ContainerGrid';
import InputText from '@/components/UI/InputText/InputText';
import MultiSelection from '@/components/UI/MultiSelection/MultiSelection';
import { DESCRIPTION, HELP_AREA } from '@/helpers/constants';
import useAppLanguage from '@/hooks/useAppLanguage';
import useToastContext from '@/hooks/useToastContext';
import {
  getCampaignInfo, getCampaignDescription, getDescriptionValidations, getOriginalValues,
} from '@/redux/Campaign/campaign.reducer';
import { getDescriptionForms } from '@/redux/Forms/forms.reducer';
import { getHeaderLanguage } from '@/redux/Header/header.reducer';
import { setAreaHelp } from '@/redux/Help/help.action';
import { Description as DescriptionType } from '@/types/Description';
import { errorMessage, maxLengthError } from './Description.utils';

const useStyles = makeStyles((theme: ThemeOptions) => ({
  disabled: { backgroundColor: theme?.cartTheme?.contentBackgroundColor },
}));

interface InspectionSectionProps {
  description: DescriptionType;
  descriptionForms: any;
  headerLanguage: string;
  checkDisabled: boolean;
  updateState: (newState: DescriptionType) => void;
  validateField: any;
  descValidations: any;
  setHelpArea: (help: string) => void;
  onBlur: (tab: string, value: string) => void;
}

const InspectionSection: React.FC<InspectionSectionProps> = (props) => {
  const { description, descriptionForms, headerLanguage, checkDisabled, updateState, validateField,
    descValidations, setHelpArea, onBlur } = props;

  const classes = useStyles();
  const { t } = useTranslation();
  const appLanguage = useAppLanguage();
  const { addToast } = useToastContext();
  const isInspectionMandatory = description?.inspectionType?.length > 0;
  const isInspectionCommentDisabled = checkDisabled || !isInspectionMandatory;

  const handleChangeInspection = (value: string[]) => {
    const newState = {
      ...description,
      inspectionType: value,
    };

    if (!value?.length) {
      newState.inspectionComment[headerLanguage] = '';
    }
    updateState(newState);
  };

  const handleDeleteInspection = (chipToDelete: string) => {
    const types = description.inspectionType.filter((type) => type !== chipToDelete);
    handleChangeInspection(types);
  };

  const handleChange = (label: string, value: string) => {
    validateField(label, value);
    const newState = {
      ...description,
      [label]: { ...description[label], [headerLanguage]: value },
    };

    updateState(newState);
  };

  return (
    <AccordionPanel
      title={t('description.inspectionAccordion')}
      handleClick={() => setHelpArea(HELP_AREA.INSPECTION)}
    >
      <ContainerGrid columns={{ xs: 1, sm: 2, md: 2, lg: 2 }} gap={16}>
        <ContainerGridItem span={{ xs: 1 }}>
          <MultiSelection
            id="demo-mutiple-chip"
            disabled={checkDisabled}
            invalid={false}
            handleDeleteItem={handleDeleteInspection}
            fieldPath={['description', 'inspectionType']}
            label={t('description.inspectionType')}
            options={descriptionForms.inspectionType.options}
            value={description.inspectionType}
            handleChange={(ev) => handleChangeInspection(ev.target.value as unknown as string[])}
            formControlClass={`${checkDisabled ? classes.disabled : ''} w-100`}
            language={appLanguage}
          />
        </ContainerGridItem>
        <ContainerGridItem span={{ xs: 1 }}>
          <div
            onClick={() => ((isInspectionCommentDisabled && !checkDisabled) ? addToast(t('validations.inspectionTypeRequired'), 'Warning') : null)}
          >
            <InputText
              id="inspection-comment"
              disabled={isInspectionCommentDisabled}
              fieldPath={['description', DESCRIPTION.INSPECTION_COMMENT]}
              otherConditionInvalid={{ and: true, condition: isInspectionMandatory }}
              multiline
              rows={5}
              maxLength={256}
              minLength={8}
              formControlClass="w-100"
              label={t('description.inspectionComment')}
              isMandatory={isInspectionMandatory}
              handleChange={(ev) => handleChange(DESCRIPTION.INSPECTION_COMMENT, ev.target.value)}
              value={description.inspectionComment[headerLanguage]}
              onBlur={(ev) => onBlur(DESCRIPTION.INSPECTION_COMMENT, ev.target.value)}
              helperText={[
                ...((!checkDisabled && errorMessage(descValidations?.[headerLanguage]?.inspectionComment, 256, t))
                  ? [errorMessage(descValidations?.[headerLanguage]?.inspectionComment, 256, t)]
                  : []),
                ...(!checkDisabled && maxLengthError(description.inspectionComment[headerLanguage], 256, t)
                  ? [maxLengthError(description.inspectionComment[headerLanguage], 256, t)]
                  : []),
              ]}
            />
          </div>
        </ContainerGridItem>
      </ContainerGrid>
    </AccordionPanel>
  );
};

const mapStateToProps = (state) => ({
  description: getCampaignDescription(state),
  descriptionForms: getDescriptionForms(state),
  originalValues: getOriginalValues(state),
  headerLanguage: getHeaderLanguage(state) || getCampaignInfo(state)?.defaultLanguage,
  descValidations: getDescriptionValidations(state),
});

const actionCreators = {
  setHelpArea: setAreaHelp,
};

export default connect(mapStateToProps, actionCreators)(InspectionSection);
